import React, { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../../../core/utils/api";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";
import { getBackgroundPatternClass } from "../../../core/utils/backgroundPatternUtils";
import { getBaseStyleClasses } from "../../../core/utils/baseStyleUtils";
import { getCharacterStyleClasses } from "../../../core/utils/characterStyleUtils";
import {
  getContainerInsetShadowClass,
  getContainerStyleClasses,
} from "../../../core/utils/containerStyleUtils";

// Custom icon components
const ChevronLeftIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
  </svg>
);

const ChevronRightIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
  </svg>
);

const ChevronUpIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 15l-7-7-7 7"></path>
  </svg>
);

// 3x3 grid icon ("apps" / "show all")
const GridMenuIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="4" y="4" width="4" height="4" rx="0.6" />
    <rect x="10" y="4" width="4" height="4" rx="0.6" />
    <rect x="16" y="4" width="4" height="4" rx="0.6" />
    <rect x="4" y="10" width="4" height="4" rx="0.6" />
    <rect x="10" y="10" width="4" height="4" rx="0.6" />
    <rect x="16" y="10" width="4" height="4" rx="0.6" />
    <rect x="4" y="16" width="4" height="4" rx="0.6" />
    <rect x="10" y="16" width="4" height="4" rx="0.6" />
    <rect x="16" y="16" width="4" height="4" rx="0.6" />
  </svg>
);

const EXPANDED_STYLE_GROUP_COLUMN_COUNT = 4;
const FALLBACK_STYLE_GROUP = Object.freeze({
  key: "others",
  label: "Others",
  order: Number.MAX_SAFE_INTEGER,
  itemOrder: Number.MAX_SAFE_INTEGER,
});

const getStyleGroupMetadata = (style) => {
  const key = String(style?.group?.key || "").trim() || FALLBACK_STYLE_GROUP.key;
  const label = String(style?.group?.label || "").trim() || FALLBACK_STYLE_GROUP.label;
  const rawOrder = Number(style?.group?.order);
  const rawItemOrder = Number(style?.group?.item_order ?? style?.group?.itemOrder);

  return {
    key,
    label,
    order: Number.isFinite(rawOrder) ? rawOrder : FALLBACK_STYLE_GROUP.order,
    itemOrder: Number.isFinite(rawItemOrder) ? rawItemOrder : FALLBACK_STYLE_GROUP.itemOrder,
  };
};

const buildExpandedStyleGroups = (styles) => {
  const groupsByKey = new Map();

  (styles || []).forEach((style, index) => {
    const group = getStyleGroupMetadata(style);
    const existing = groupsByKey.get(group.key);
    if (existing) {
      existing.styleEntries.push({ style, index, itemOrder: group.itemOrder });
      existing.firstStyleIndex = Math.min(existing.firstStyleIndex, index);
      return;
    }
    groupsByKey.set(group.key, {
      ...group,
      firstStyleIndex: index,
      styleEntries: [{ style, index, itemOrder: group.itemOrder }],
    });
  });

  return Array.from(groupsByKey.values())
    .map((group) => ({
      ...group,
      styles: group.styleEntries
        .slice()
        .sort((a, b) => {
          if (a.itemOrder !== b.itemOrder) return a.itemOrder - b.itemOrder;
          return a.index - b.index;
        })
        .map((entry) => entry.style),
    }))
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      if (a.firstStyleIndex !== b.firstStyleIndex) {
        return a.firstStyleIndex - b.firstStyleIndex;
      }
      return a.label.localeCompare(b.label);
    });
};

const distributeGroupsIntoColumns = (groups, columnCount = EXPANDED_STYLE_GROUP_COLUMN_COUNT) => {
  if (!Array.isArray(groups) || groups.length === 0) return [[]];

  // Greedy packing keeps extra groups below the currently shortest column,
  // which avoids large empty gaps when groups have uneven sizes.
  const columns = Array.from(
    { length: Math.max(1, Math.min(columnCount, groups.length)) },
    () => ({ height: 0, groups: [] })
  );

  groups.forEach((group) => {
    const targetColumnIndex = columns.reduce((shortestIndex, column, index) => (
      column.height < columns[shortestIndex].height ? index : shortestIndex
    ), 0);
    const groupHeightScore = 1.5 + (group?.styles?.length || 0);

    columns[targetColumnIndex].groups.push(group);
    columns[targetColumnIndex].height += groupHeightScore;
  });

  return columns.map((column) => column.groups).filter((column) => column.length > 0);
};

const StyleSelection = ({
  selectedStyle,
  onStyleSelect,
  itemsPerPage = 4,
  variant = "grid",
  useColorAccents = true,
  styles: stylesOverride = null,
  onPaginationChange = null,
}) => {
  const [internalStyles, setInternalStyles] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [internalError, setInternalError] = useState(null);

  const ITEMS_PER_PAGE = Math.max(1, itemsPerPage); // Number of items to show per row
  const isCompact = variant === "compact";
  const isControlled = Array.isArray(stylesOverride);
  const styles = isControlled ? stylesOverride : internalStyles;
  const loading = isControlled ? false : internalLoading;
  const error = isControlled ? null : internalError;

  // Expanded view only exists for compact selector UI.
  useEffect(() => {
    if (!isCompact && isExpanded) {
      setIsExpanded(false);
    }
  }, [isCompact, isExpanded]);

  useEffect(() => {
    if (isControlled) return;
    fetchStyles();
  }, [isControlled]);

  const fetchStyles = async () => {
    try {
      setInternalLoading(true);
      const response = await apiFetch("/api/style-presets/");
      if (!response.ok) {
        throw new Error("Failed to fetch styles");
      }
      const data = await response.json();
      setInternalStyles(data);
    } catch (err) {
      console.error("Error fetching styles:", err);
      setInternalError(err.message);
    } finally {
      setInternalLoading(false);
    }
  };

  const expandedStyleGroups = useMemo(() => buildExpandedStyleGroups(styles), [styles]);
  const expandedStyleColumns = useMemo(
    () => distributeGroupsIntoColumns(expandedStyleGroups),
    [expandedStyleGroups]
  );

  // Group styles into families for compact mode: one card per family, variant dots.
  const familyCards = useMemo(() => {
    if (!styles?.length) return [];
    const familyMap = new Map();
    const order = [];
    for (const style of styles) {
      const key = style.family?.key || style.value;
      if (!familyMap.has(key)) {
        familyMap.set(key, {
          key,
          primary: style.family?.primary || style.value,
          membersMeta: style.family?.members || [{ preset: style.value, name: style.name, colors: [] }],
          styles: [],
        });
        order.push(key);
      }
      familyMap.get(key).styles.push(style);
    }
    return order.map((k) => familyMap.get(k));
  }, [styles]);

  const displayItems = isCompact ? familyCards : styles;
  const totalPages = Math.ceil(displayItems.length / ITEMS_PER_PAGE);
  const currentPageItems = displayItems.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  // Keep page in range if list size changes (e.g. filters, async load, etc.)
  useEffect(() => {
    const maxPage = Math.max(0, totalPages - 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, totalPages]);

  // Surface pagination state to parent (useful for showing "(x/y)" next to labels).
  useEffect(() => {
    if (typeof onPaginationChange !== "function") return;
    const maxPage = Math.max(0, totalPages - 1);
    const safePage = Math.min(currentPage, maxPage);
    onPaginationChange({
      currentPage: safePage,
      totalPages,
      itemsPerPage: ITEMS_PER_PAGE,
      totalItems: displayItems.length,
      isExpanded,
    });
  }, [onPaginationChange, currentPage, totalPages, ITEMS_PER_PAGE, displayItems.length, isExpanded]);

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getStyleColors = (styleValue) => {
    const colorMap = {
      midnight: { bg: "bg-blue-100 dark:bg-blue-900/20", border: "border-blue-300 dark:border-blue-700", text: "text-blue-700 dark:text-blue-200", initial: "bg-blue-600 dark:bg-blue-500" },
      eclipse: { bg: "bg-rose-100 dark:bg-rose-900/20", border: "border-rose-300 dark:border-rose-700", text: "text-rose-700 dark:text-rose-200", initial: "bg-rose-800 dark:bg-rose-600" },
      twilight: { bg: "bg-teal-100 dark:bg-teal-900/20", border: "border-teal-300 dark:border-teal-700", text: "text-teal-700 dark:text-teal-200", initial: "bg-teal-700 dark:bg-teal-500" },
      autumn: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-700 dark:text-amber-200", initial: "bg-amber-600 dark:bg-amber-500" },
      autumn_dark: { bg: "bg-orange-100 dark:bg-orange-900/20", border: "border-orange-300 dark:border-orange-700", text: "text-orange-700 dark:text-orange-200", initial: "bg-orange-700 dark:bg-orange-600" },
      sky: { bg: "bg-sky-100 dark:bg-sky-900/20", border: "border-sky-300 dark:border-sky-700", text: "text-sky-700 dark:text-sky-200", initial: "bg-yellow-500 dark:bg-yellow-400" },
      night_sky: { bg: "bg-indigo-100 dark:bg-indigo-900/20", border: "border-indigo-300 dark:border-indigo-700", text: "text-indigo-700 dark:text-indigo-200", initial: "bg-amber-200 dark:bg-amber-100" },
      notebook: { bg: "bg-red-100 dark:bg-red-900/20", border: "border-red-300 dark:border-red-700", text: "text-red-700 dark:text-red-200", initial: "bg-red-600 dark:bg-red-500" },
      qadim: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-red-300 dark:border-red-700", text: "text-red-800 dark:text-red-200", initial: "bg-red-700 dark:bg-red-500" },
      azraq: { bg: "bg-blue-50 dark:bg-blue-900/20", border: "border-blue-300 dark:border-blue-700", text: "text-blue-800 dark:text-blue-200", initial: "bg-blue-900 dark:bg-blue-600" },
      dhahab: { bg: "bg-neutral-100 dark:bg-neutral-900/20", border: "border-yellow-300 dark:border-yellow-700", text: "text-yellow-800 dark:text-yellow-200", initial: "bg-yellow-500 dark:bg-yellow-400" },
      nuhas: { bg: "bg-orange-100 dark:bg-orange-900/20", border: "border-orange-300 dark:border-orange-700", text: "text-orange-800 dark:text-orange-200", initial: "bg-orange-700 dark:bg-orange-500" },
      clean: { bg: "bg-blue-100 dark:bg-blue-900/20", border: "border-blue-300 dark:border-blue-700", text: "text-blue-700 dark:text-blue-200", initial: "bg-blue-600 dark:bg-blue-500" },
      crisp: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-emerald-700 dark:bg-emerald-600" },
      dark_red: { bg: "bg-red-100 dark:bg-red-900/20", border: "border-red-300 dark:border-red-700", text: "text-red-800 dark:text-red-200", initial: "bg-red-900 dark:bg-red-700" },
      onyx: { bg: "bg-gray-100 dark:bg-dark-800", border: "border-gray-300 dark:border-dark-600", text: "text-gray-800 dark:text-dark-100", initial: "bg-gray-900 dark:bg-dark-900" },
      journal: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-emerald-700 dark:bg-emerald-600" },
      adventure: { bg: "bg-red-100 dark:bg-red-900/20", border: "border-red-300 dark:border-red-700", text: "text-red-700 dark:text-red-200", initial: "bg-red-600 dark:bg-red-500" },
      expedition: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-700 dark:text-amber-200", initial: "bg-amber-600 dark:bg-amber-500" },
      editorial: { bg: "bg-gray-100 dark:bg-dark-800", border: "border-gray-300 dark:border-dark-600", text: "text-gray-700 dark:text-dark-200", initial: "bg-gray-600 dark:bg-dark-400" },
      ink: { bg: "bg-slate-100 dark:bg-slate-900/20", border: "border-slate-300 dark:border-slate-700", text: "text-slate-700 dark:text-slate-200", initial: "bg-slate-800 dark:bg-slate-600" },
      gazette: { bg: "bg-pink-100 dark:bg-pink-900/20", border: "border-pink-300 dark:border-pink-700", text: "text-pink-700 dark:text-pink-200", initial: "bg-pink-900 dark:bg-pink-700" },
      // Backwards-compatible alias (older saved values)
      white_gray: { bg: "bg-gray-100 dark:bg-dark-800", border: "border-gray-300 dark:border-dark-600", text: "text-gray-700 dark:text-dark-200", initial: "bg-gray-600 dark:bg-dark-400" },
      // Legacy alias (backend now uses "notebook")
      research: { bg: "bg-red-100 dark:bg-red-900/20", border: "border-red-300 dark:border-red-700", text: "text-red-700 dark:text-red-200", initial: "bg-red-600 dark:bg-red-500" },
      creative: { bg: "bg-purple-100 dark:bg-purple-900/20", border: "border-purple-300 dark:border-purple-700", text: "text-purple-700 dark:text-purple-200", initial: "bg-purple-600 dark:bg-purple-500" },
      pulse: { bg: "bg-fuchsia-100 dark:bg-fuchsia-900/20", border: "border-fuchsia-300 dark:border-fuchsia-700", text: "text-fuchsia-700 dark:text-fuchsia-200", initial: "bg-fuchsia-600 dark:bg-fuchsia-500" },
      ion: { bg: "bg-lime-100 dark:bg-lime-900/20", border: "border-lime-300 dark:border-lime-700", text: "text-lime-700 dark:text-lime-200", initial: "bg-lime-600 dark:bg-lime-500" },
      minimal: { bg: "bg-gray-100 dark:bg-dark-800", border: "border-gray-300 dark:border-dark-600", text: "text-gray-700 dark:text-dark-200", initial: "bg-gray-600 dark:bg-dark-400" },
      bloom: { bg: "bg-yellow-100 dark:bg-yellow-900/20", border: "border-yellow-300 dark:border-yellow-700", text: "text-yellow-700 dark:text-yellow-200", initial: "bg-yellow-600 dark:bg-yellow-500" },
      sepia: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-700 dark:text-amber-200", initial: "bg-amber-600 dark:bg-amber-500" },
      lavender: { bg: "bg-purple-100 dark:bg-purple-900/20", border: "border-purple-300 dark:border-purple-700", text: "text-purple-700 dark:text-purple-200", initial: "bg-purple-600 dark:bg-purple-500" },
      // Mint: airy white hero/component surfaces with a lighter sage-green
      // title — chip leans on the soft mint/emerald palette to hint at the
      // family's freshest, brightest variant.
      mint: { bg: "bg-emerald-50 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-emerald-500 dark:bg-emerald-400" },
      // Backwards-compatible alias (older saved values)
      radiant: { bg: "bg-yellow-100 dark:bg-yellow-900/20", border: "border-yellow-300 dark:border-yellow-700", text: "text-yellow-700 dark:text-yellow-200", initial: "bg-yellow-600 dark:bg-yellow-500" },
      dark: { bg: "bg-gray-100 dark:bg-dark-900", border: "border-gray-300 dark:border-dark-700", text: "text-gray-700 dark:text-dark-100", initial: "bg-gray-900 dark:bg-dark-500" },
      velvet: { bg: "bg-red-100 dark:bg-red-900/20", border: "border-red-300 dark:border-red-700", text: "text-red-700 dark:text-red-200", initial: "bg-orange-600 dark:bg-orange-500" },
      fifty: { bg: "bg-blue-100 dark:bg-blue-900/20", border: "border-blue-300 dark:border-blue-700", text: "text-blue-700 dark:text-blue-200", initial: "bg-blue-600 dark:bg-blue-500" },
      sixty: { bg: "bg-red-100 dark:bg-red-900/20", border: "border-red-300 dark:border-red-700", text: "text-red-700 dark:text-red-200", initial: "bg-orange-600 dark:bg-orange-500" },
      seventy: { bg: "bg-purple-100 dark:bg-purple-900/20", border: "border-purple-300 dark:border-purple-700", text: "text-purple-700 dark:text-purple-200", initial: "bg-purple-600 dark:bg-purple-500" },
      surf: { bg: "bg-slate-100 dark:bg-dark-900/20", border: "border-slate-300 dark:border-dark-700", text: "text-slate-700 dark:text-dark-200", initial: "bg-emerald-600 dark:bg-emerald-500" },
      hibiscus: { bg: "bg-slate-100 dark:bg-dark-900/20", border: "border-slate-300 dark:border-dark-700", text: "text-slate-700 dark:text-dark-200", initial: "bg-emerald-600 dark:bg-emerald-500" },
      coastal: { bg: "bg-cyan-100 dark:bg-cyan-900/20", border: "border-cyan-300 dark:border-cyan-700", text: "text-cyan-700 dark:text-cyan-200", initial: "bg-cyan-500 dark:bg-cyan-400" },
      // Backwards-compatible alias (older saved values; "fifty" used to map to "surf")
      // fifty is now its own preset (sixty-family blue variant) — see entry above
      verdant: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-emerald-600 dark:bg-emerald-500" },
      forest: { bg: "bg-green-100 dark:bg-green-900/20", border: "border-green-300 dark:border-green-700", text: "text-green-700 dark:text-green-200", initial: "bg-green-700 dark:bg-green-600" },
      arctic: { bg: "bg-sky-100 dark:bg-sky-900/20", border: "border-sky-300 dark:border-sky-700", text: "text-sky-700 dark:text-sky-200", initial: "bg-sky-600 dark:bg-sky-500" },
      ocean: { bg: "bg-indigo-100 dark:bg-indigo-900/20", border: "border-indigo-300 dark:border-indigo-700", text: "text-indigo-700 dark:text-indigo-200", initial: "bg-indigo-700 dark:bg-indigo-600" },
      elegant: { bg: "bg-rose-100 dark:bg-rose-900/20", border: "border-rose-300 dark:border-rose-700", text: "text-rose-700 dark:text-rose-200", initial: "bg-rose-600 dark:bg-rose-500" },
      sienna: { bg: "bg-orange-100 dark:bg-orange-900/20", border: "border-orange-300 dark:border-orange-700", text: "text-orange-700 dark:text-orange-200", initial: "bg-orange-700 dark:bg-orange-600" },
      diffused: { bg: "bg-slate-100 dark:bg-slate-900/20", border: "border-slate-300 dark:border-slate-700", text: "text-slate-700 dark:text-slate-200", initial: "bg-slate-600 dark:bg-slate-500" },
      sand: { bg: "bg-stone-100 dark:bg-stone-900/20", border: "border-stone-300 dark:border-stone-700", text: "text-stone-700 dark:text-stone-200", initial: "bg-stone-600 dark:bg-stone-500" },
      diffused_light: { bg: "bg-slate-50 dark:bg-slate-900/20", border: "border-rose-200 dark:border-rose-700", text: "text-rose-700 dark:text-rose-200", initial: "bg-rose-300 dark:bg-rose-400" },
      sand_light: { bg: "bg-stone-50 dark:bg-stone-900/20", border: "border-lime-200 dark:border-lime-700", text: "text-lime-700 dark:text-lime-200", initial: "bg-lime-300 dark:bg-lime-400" },
      voyage: { bg: "bg-slate-100 dark:bg-slate-900/20", border: "border-slate-300 dark:border-slate-700", text: "text-slate-700 dark:text-slate-200", initial: "bg-slate-600 dark:bg-slate-500" },
      journey: { bg: "bg-sky-50 dark:bg-sky-900/20", border: "border-sky-300 dark:border-sky-700", text: "text-sky-700 dark:text-sky-200", initial: "bg-sky-600 dark:bg-sky-500" },
      ember: { bg: "bg-amber-50 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-700 dark:text-amber-200", initial: "bg-amber-600 dark:bg-amber-500" },
      nostalgia: { bg: "bg-purple-50 dark:bg-purple-900/20", border: "border-purple-300 dark:border-purple-700", text: "text-purple-800 dark:text-purple-200", initial: "bg-purple-700 dark:bg-purple-500" },
      vesper: { bg: "bg-blue-50 dark:bg-blue-900/20", border: "border-blue-300 dark:border-blue-700", text: "text-blue-800 dark:text-blue-200", initial: "bg-blue-800 dark:bg-blue-500" },
      heirloom: { bg: "bg-rose-50 dark:bg-rose-900/20", border: "border-rose-300 dark:border-rose-700", text: "text-rose-800 dark:text-rose-200", initial: "bg-rose-700 dark:bg-rose-500" },
      patina: { bg: "bg-emerald-50 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-800 dark:text-emerald-200", initial: "bg-emerald-700 dark:bg-emerald-500" },
      cosmo: { bg: "bg-violet-100 dark:bg-violet-900/20", border: "border-violet-300 dark:border-violet-700", text: "text-violet-700 dark:text-violet-200", initial: "bg-violet-600 dark:bg-violet-500" },
      library: { bg: "bg-green-100 dark:bg-green-900/20", border: "border-green-300 dark:border-green-700", text: "text-green-700 dark:text-green-200", initial: "bg-green-600 dark:bg-green-500" },
      atlas: { bg: "bg-sky-100 dark:bg-sky-900/20", border: "border-sky-300 dark:border-sky-700", text: "text-sky-700 dark:text-sky-200", initial: "bg-sky-800 dark:bg-sky-600" },
      codex: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-emerald-800 dark:bg-emerald-600" },
      golden: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-700 dark:text-amber-200", initial: "bg-amber-500 dark:bg-amber-400" },
      silver: { bg: "bg-slate-100 dark:bg-slate-900/20", border: "border-slate-300 dark:border-slate-700", text: "text-slate-700 dark:text-slate-200", initial: "bg-slate-500 dark:bg-slate-400" },
      bronze: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-700 dark:text-amber-200", initial: "bg-amber-700 dark:bg-amber-600" },
      umbra: { bg: "bg-neutral-100 dark:bg-neutral-900/20", border: "border-neutral-300 dark:border-neutral-700", text: "text-neutral-700 dark:text-neutral-200", initial: "bg-neutral-800 dark:bg-neutral-600" },
      alabaster: { bg: "bg-slate-50 dark:bg-slate-900/20", border: "border-slate-200 dark:border-slate-700", text: "text-slate-700 dark:text-slate-200", initial: "bg-slate-500 dark:bg-slate-400" },
      // Backwards-compatible alias for older saved preset values.
      pure: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-700 dark:text-amber-200", initial: "bg-amber-500 dark:bg-amber-400" },
      tech: { bg: "bg-indigo-100 dark:bg-indigo-900/20", border: "border-indigo-300 dark:border-indigo-700", text: "text-indigo-700 dark:text-indigo-200", initial: "bg-indigo-600 dark:bg-indigo-500" },
      // Lab's signature surface is the dark slate "Featured Projects" panel
      // with a mint Bebas Neue title. The chip leans on emerald to echo that
      // mint title strand on a dark slate initial swatch — reads as the
      // family's "lab notebook" palette in both themes.
      lab: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-slate-800 dark:bg-slate-700" },
      // ``atom`` is the lab-family variant whose title accent is a warm
      // light red and whose hero swaps the DNA-helix for atom-orbits.
      // Same slate initial swatch (the dark panel is the family's
      // signature surface); chip leans on rose/red tones to echo the
      // electron / title strand on both themes.
      atom: { bg: "bg-rose-100 dark:bg-rose-900/20", border: "border-rose-300 dark:border-rose-700", text: "text-rose-700 dark:text-rose-200", initial: "bg-slate-800 dark:bg-slate-700" },
      // ``fourier`` is the lab-family variant that keeps the canonical
      // mint title accent and only swaps the hero pattern to the
      // fourier-series epicycle visualization. Chip mirrors the canonical
      // ``lab`` swatch (emerald/mint on a slate initial) since the title
      // accent is shared with ``lab``.
      fourier: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-slate-800 dark:bg-slate-700" },
      window: { bg: "bg-sky-50 dark:bg-sky-900/20", border: "border-sky-200 dark:border-sky-700", text: "text-sky-700 dark:text-sky-200", initial: "bg-sky-300 dark:bg-sky-400" },
      glass: { bg: "bg-cyan-100 dark:bg-cyan-900/20", border: "border-cyan-300 dark:border-cyan-700", text: "text-cyan-800 dark:text-cyan-200", initial: "bg-neutral-900 dark:bg-neutral-950" },
      // Backwards-compatible alias for older saved preset values.
      window_glass: { bg: "bg-sky-50 dark:bg-sky-900/20", border: "border-sky-200 dark:border-sky-700", text: "text-sky-700 dark:text-sky-200", initial: "bg-sky-300 dark:bg-sky-400" },
      // Blurred reads as a deep teal/slate atmospheric blur with soft sky-blue
      // highlights — accent leans on those blue tones rather than the small
      // red highlight reserved for the mobile menu icon and footer link.
      blurred: { bg: "bg-slate-100 dark:bg-slate-900/20", border: "border-slate-300 dark:border-slate-700", text: "text-slate-800 dark:text-slate-200", initial: "bg-slate-700 dark:bg-slate-500" },
      // Aurora: forest-green bokeh-drift atmosphere with mint highlights and
      // a violet accent reserved for the mobile menu icon and footer link —
      // chip leans on the green palette the gradient is built around.
      aurora: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-800 dark:text-emerald-200", initial: "bg-emerald-700 dark:bg-emerald-500" },
      // Dusk: plum/lavender bokeh-drift atmosphere with a warm gold accent
      // reserved for the mobile menu icon and footer link — chip leans on
      // the violet palette rather than the sharp amber highlight.
      dusk: { bg: "bg-violet-100 dark:bg-violet-900/20", border: "border-violet-300 dark:border-violet-700", text: "text-violet-800 dark:text-violet-200", initial: "bg-violet-700 dark:bg-violet-500" },
      // Constellations (canonical): deeper midnight sky + warm
      // starlight-gold ribbons. Chip leans on the gold palette so it
      // reads as the family's "candle-lit night sky" identity; the
      // initial dot stays on the deep midnight navy so the swatch keeps
      // the family's constellation backdrop.
      constellations: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-800 dark:text-amber-200", initial: "bg-slate-900 dark:bg-slate-700" },
      // Lyra: original navy + light-blue palette captured from
      // fernando's p52 page (previously exposed as the canonical
      // constellations preset). Chip leans on the sky palette to
      // telegraph the starlit-navy identity.
      lyra: { bg: "bg-sky-100 dark:bg-sky-900/20", border: "border-sky-300 dark:border-sky-700", text: "text-sky-800 dark:text-sky-200", initial: "bg-slate-900 dark:bg-slate-700" },
      // Polaris: high-key dawn-light sky variant with white-blue starshine.
      // Chip leans on the bright sky palette and lands the initial on the
      // deep navy text/button color so the dot stays visible against the
      // light card surface.
      polaris: { bg: "bg-sky-50 dark:bg-sky-900/20", border: "border-sky-200 dark:border-sky-700", text: "text-sky-800 dark:text-sky-200", initial: "bg-slate-700 dark:bg-slate-500" },
      // Videogame: arcade-flavoured retro look on a deep-navy canvas
      // with neon-green CTA pills and an animated game-of-life navbar
      // (red/yellow) + footer (mint/magenta). The dominant accent
      // across hero buttons + page neon stops is lime green, so the
      // chip leans on the emerald palette and lands the initial on
      // the deep navy page primary so the dot reads as the arcade
      // canvas.
      videogame: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-slate-900 dark:bg-slate-700" },
      // Arcade: videogame variant with the canonical's #101721 ↔
      // #ffffff slots swapped, so the page canvas reads as white with
      // deep-navy ink. Chip leans on a lighter emerald background
      // (mirroring the lime CTA accent the family is built around) and
      // lands the initial on the now-deep-navy ink colour so the dot
      // telegraphs the inverted "ink-on-paper" arcade look.
      arcade: { bg: "bg-emerald-50 dark:bg-emerald-900/20", border: "border-emerald-200 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-slate-900 dark:bg-slate-500" },
      // Wire: indigo-violet plasma-waves gradient (p70). Chip pairs the
      // deep indigo page stop with the soft violet highlight.
      wire: { bg: "bg-violet-100 dark:bg-violet-900/20", border: "border-violet-300 dark:border-violet-700", text: "text-violet-800 dark:text-violet-200", initial: "bg-indigo-950 dark:bg-indigo-900" },
      // Wire Trace: cyan on deep slate — chip mirrors trace + midnight stops.
      wire_trace: { bg: "bg-cyan-100 dark:bg-cyan-900/20", border: "border-cyan-300 dark:border-cyan-700", text: "text-cyan-800 dark:text-cyan-200", initial: "bg-slate-950 dark:bg-slate-900" },
      // Wire Heat: amber on stone — warm chip with dark umber initial.
      wire_heat: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-900 dark:text-amber-200", initial: "bg-stone-800 dark:bg-stone-700" },
      // Wire Ink: deep fuchsia-purple plasma — chip on purple tones.
      wire_ink: { bg: "bg-purple-100 dark:bg-purple-900/20", border: "border-purple-300 dark:border-purple-700", text: "text-purple-800 dark:text-purple-200", initial: "bg-purple-950 dark:bg-purple-900" },
      flierflies: { bg: "bg-yellow-100 dark:bg-yellow-900/20", border: "border-yellow-300 dark:border-yellow-700", text: "text-yellow-800 dark:text-yellow-200", initial: "bg-indigo-950 dark:bg-indigo-900" },
      foxfire: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-800 dark:text-emerald-200", initial: "bg-emerald-950 dark:bg-emerald-900" },
      lava_lamp: { bg: "bg-red-100 dark:bg-red-900/20", border: "border-red-300 dark:border-red-700", text: "text-red-800 dark:text-red-200", initial: "bg-stone-950 dark:bg-stone-900" },
      magma: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-800 dark:text-amber-200", initial: "bg-amber-950 dark:bg-amber-900" },
      plasma: { bg: "bg-cyan-100 dark:bg-cyan-900/20", border: "border-cyan-300 dark:border-cyan-700", text: "text-cyan-800 dark:text-cyan-200", initial: "bg-indigo-950 dark:bg-indigo-900" },
      shades: { bg: "bg-slate-100 dark:bg-slate-900/20", border: "border-slate-300 dark:border-slate-700", text: "text-slate-700 dark:text-slate-200", initial: "bg-slate-600 dark:bg-slate-500" },
      blush: { bg: "bg-rose-100 dark:bg-rose-900/20", border: "border-rose-300 dark:border-rose-700", text: "text-rose-700 dark:text-rose-200", initial: "bg-rose-400 dark:bg-rose-500" },
      moss: { bg: "bg-emerald-100 dark:bg-emerald-900/20", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-200", initial: "bg-emerald-600 dark:bg-emerald-500" },
      desert: { bg: "bg-amber-100 dark:bg-amber-900/20", border: "border-amber-300 dark:border-amber-700", text: "text-amber-700 dark:text-amber-200", initial: "bg-amber-700 dark:bg-amber-600" },
      oasis: { bg: "bg-teal-100 dark:bg-teal-900/20", border: "border-teal-300 dark:border-teal-700", text: "text-teal-700 dark:text-teal-200", initial: "bg-teal-600 dark:bg-teal-500" },
      mesa: { bg: "bg-orange-100 dark:bg-orange-900/20", border: "border-orange-300 dark:border-orange-700", text: "text-orange-700 dark:text-orange-200", initial: "bg-orange-800 dark:bg-orange-600" },
      dune: { bg: "bg-violet-100 dark:bg-violet-900/20", border: "border-violet-300 dark:border-violet-700", text: "text-violet-700 dark:text-violet-200", initial: "bg-violet-600 dark:bg-violet-500" },
    };
    return colorMap[styleValue] || colorMap.midnight;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className={`ml-2 ${typography.body} ${textColors.primary}`}>Loading styles...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className={`${typography.body} text-red-600 dark:text-red-400 mb-2`}>Failed to load styles</div>
        <button
          onClick={fetchStyles}
          className={`px-4 py-2 ${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText} rounded-md ${typography.button}`}
        >
          Try Again
        </button>
      </div>
    );
  }

  const computeStyledCardProps = (labelPreviewData, isSelectedState) => {
    const title = labelPreviewData?.title || null;
    const component = labelPreviewData?.component || null;
    const componentBgPatternRaw = component?.background?.background_pattern || null;
    // Force the background pattern to render fully opaque on the chip preview.
    // Some presets ship transparent backgrounds (opacity=0) so the underlying
    // page chrome shows through, but in the chip we want the colour to read
    // clearly regardless of the preset's intent.
    const componentBgPattern =
      componentBgPatternRaw && typeof componentBgPatternRaw === "object"
        ? { ...componentBgPatternRaw, opacity: 100 }
        : componentBgPatternRaw;
    const componentBaseStyleRaw = component?.base_style || null;
    const componentContainerStyle = component?.container_style || null;
    const titleCharacterStyle = title?.character_style || null;
    const titleBaseStyleRaw = title?.base_style || null;

    const componentBaseStyle =
      componentBaseStyleRaw && typeof componentBaseStyleRaw === "object"
        ? { ...componentBaseStyleRaw, border_enabled: false }
        : componentBaseStyleRaw;
    const componentContainerStyleNoShadow =
      componentContainerStyle && typeof componentContainerStyle === "object"
        ? { ...componentContainerStyle, shadow_internal_enabled: false, shadow_internal_size: 0 }
        : componentContainerStyle;
    const componentBaseStyleNoShadowGlow =
      componentBaseStyle && typeof componentBaseStyle === "object"
        ? { ...componentBaseStyle, shadow_enabled: false, shadow_custom_enabled: false, glow_enabled: false }
        : componentBaseStyle;

    const { colorClass: bgClass = "", colorStyle: bgStyle = {} } = getBackgroundPatternClass(componentBgPattern);
    const { className: componentBaseClassesRaw = "", style: componentBaseStylesRaw = {} } =
      getBaseStyleClasses(componentBaseStyleNoShadowGlow);
    const rawContainerClasses = getContainerStyleClasses(componentContainerStyleNoShadow);
    const needsFallbackRounding = !/\brounded(-(?!none)\S+)?\b/.test(rawContainerClasses || "");
    const componentContainerClasses = `${rawContainerClasses} ${needsFallbackRounding ? "rounded-xl" : ""}`.trim();
    const insetShadowClass = getContainerInsetShadowClass(componentContainerStyleNoShadow);
    const hasRoundedBorder = /\brounded(-(?!none)\S+)?\b/.test(componentContainerClasses || "");
    const overflowClass = hasRoundedBorder ? "overflow-hidden" : "overflow-visible";
    const componentBaseClasses = String(componentBaseClassesRaw || "")
      .split(" ")
      .filter((c) => c && !/^(shadow|shadow-|drop-shadow)/.test(c))
      .join(" ");
    const componentBaseStyles = { ...(componentBaseStylesRaw || {}) };
    if (componentBaseStyles && typeof componentBaseStyles === "object" && "boxShadow" in componentBaseStyles) {
      delete componentBaseStyles.boxShadow;
    }

    const { className: rawCharClasses = "", style: charStyles = {} } = getCharacterStyleClasses(titleCharacterStyle, null);
    const cleanCharClasses = String(rawCharClasses || "")
      .split(" ")
      .filter((c) => c && !/^(flex|justify-|items-|text-left|text-right|text-center|text-justify)/.test(c) && c.trim() !== "none")
      .filter((c) => c && !/^leading-/.test(c))
      .join(" ");
    // Cap the title's text-stroke (rendered from BaseStyle.border_* when
    // ``isText`` is true) at width 1 so heavy outlines from presets like
    // Bebas Neue don't dominate the chip. We only clamp when it exceeds 1 —
    // we don't enable the outline if the preset has it disabled.
    const titleBaseStyle =
      titleBaseStyleRaw &&
      typeof titleBaseStyleRaw === "object" &&
      typeof titleBaseStyleRaw.border_width === "number" &&
      titleBaseStyleRaw.border_width > 1
        ? { ...titleBaseStyleRaw, border_width: 1 }
        : titleBaseStyleRaw;
    const { className: titleBaseClasses = "", style: titleBaseStyles = {} } = getBaseStyleClasses(titleBaseStyle, true);
    const selectionOutlineClasses = isSelectedState
      ? "outline outline-2 outline-blue-500 outline-offset-2"
      : "outline outline-1 outline-transparent outline-offset-2";

    return {
      cardClassName: [
        "relative", "w-full", "flex", "items-center", "p-3",
        overflowClass, componentContainerClasses, componentBaseClasses, selectionOutlineClasses,
      ].filter(Boolean).join(" "),
      cardStyle: componentBaseStyles,
      bgClass,
      bgStyle,
      insetShadowClass,
      titleClassName: `relative z-10 leading-none ${titleBaseClasses} ${cleanCharClasses}`.trim(),
      titleStyle: { ...titleBaseStyles, ...charStyles },
    };
  };

  const renderFamilyCard = (family) => {
    const selectedMember = family.styles.find((s) => s.value === selectedStyle?.value);
    const primaryMember = family.styles.find((s) => s.value === family.primary) || family.styles[0];
    const activeStyle = selectedMember || primaryMember;
    const isActive = !!selectedMember;
    const hasVariants = family.membersMeta.length > 1;

    const labelPreview = activeStyle?.label_preview || null;
    const styledProps = !useColorAccents && labelPreview ? computeStyledCardProps(labelPreview, isActive) : null;
    const colors = useColorAccents ? getStyleColors(activeStyle.value) : null;

    const variantDots = hasVariants ? (
      <div className="ml-auto flex items-center gap-1.5 shrink-0">
        {family.membersMeta.map((member) => {
          const memberStyle = family.styles.find((s) => s.value === member.preset);
          if (!memberStyle) return null;
          const isMemberSelected = isActive && memberStyle.value === activeStyle.value;
          const swatchColors = member.colors || [];
          const dotBg =
            swatchColors.length >= 2
              ? `linear-gradient(135deg, ${swatchColors[0]}, ${swatchColors[1]})`
              : swatchColors[0] || "#888";
          return (
            <button
              key={member.preset}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStyleSelect(memberStyle);
              }}
              title={member.name}
              aria-label={`${member.name} variant`}
              className={`w-3.5 h-3.5 rounded-full transition-all duration-150 ${
                isMemberSelected
                  ? "ring-[1.5px] ring-blue-500 ring-offset-1 scale-110"
                  : "opacity-70 hover:opacity-100 hover:scale-125"
              }`}
              style={{ background: dotBg }}
            />
          );
        })}
      </div>
    ) : null;

    return (
      <div
        key={family.key}
        onClick={() => onStyleSelect(activeStyle)}
        className={
          styledProps
            ? `cursor-pointer transition-all ${typography.bodySmall} ${styledProps.cardClassName}`.trim()
            : `cursor-pointer transition-all ${typography.bodySmall} rounded-md border p-3 w-full flex items-center ${
                isActive
                  ? useColorAccents
                    ? `${colors.border} ${colors.bg} ring-2 ring-offset-2 ring-blue-500`
                    : `${borders.default} ${surfaces.secondary} ring-2 ring-offset-2 ring-blue-500`
                  : useColorAccents
                    ? `border-gray-200 dark:border-gray-700 ${surfaces.primary} hover:${colors.bg} hover:${colors.border}`
                    : `${borders.default} ${surfaces.primary} ${surfaces.hover}`
              }`.trim()
        }
        style={styledProps ? styledProps.cardStyle : undefined}
      >
        {styledProps ? (
          <>
            {(styledProps.bgClass || styledProps.insetShadowClass) && (
              <div
                className={`absolute inset-0 w-full h-full z-0 ${styledProps.bgClass} ${styledProps.insetShadowClass}`.trim()}
                style={styledProps.bgStyle}
              />
            )}
            <div className="relative z-10 flex w-full items-center gap-2">
              <span className={styledProps.titleClassName} style={styledProps.titleStyle} title={activeStyle.name}>
                {activeStyle.name}
              </span>
              {variantDots}
            </div>
          </>
        ) : (
          <div className="flex w-full items-center gap-2">
            {useColorAccents ? <span className={`h-2 w-2 rounded-full ${colors.initial}`} /> : null}
            <span className={`${textColors.primary} ${isActive ? "font-semibold" : "font-medium"}`}>
              {activeStyle.name}
            </span>
            {variantDots}
          </div>
        )}
      </div>
    );
  };

  const renderStyleCard = (style) => {
    const colors = getStyleColors(style.value);
    const isSelected = selectedStyle?.value === style.value;
    const labelPreview = style?.label_preview || null;
    const baseClasses = isCompact
      ? `cursor-pointer transition-all ${typography.bodySmall}`
      : "relative cursor-pointer rounded-lg border-2 p-3 transition-all duration-200 hover:shadow";
    const stateClasses = isSelected
      ? useColorAccents
        ? `${colors.border} ${colors.bg} ring-2 ring-offset-2 ring-blue-500`
        : `${borders.default} ${surfaces.secondary} ring-2 ring-offset-2 ring-blue-500`
      : useColorAccents
        ? `border-gray-200 dark:border-gray-700 ${surfaces.primary} hover:${colors.bg} hover:${colors.border}`
        : `${borders.default} ${surfaces.primary} ${surfaces.hover}`;

    const renderStyledCompactCard = () => {
      const title = labelPreview?.title || null;
      const component = labelPreview?.component || null;
      const componentBgPatternRaw = component?.background?.background_pattern || null;
      // Mirror the family-card chip: force the chip's component background
      // to render fully opaque so transparent presets still read clearly.
      const componentBgPattern =
        componentBgPatternRaw && typeof componentBgPatternRaw === "object"
          ? { ...componentBgPatternRaw, opacity: 100 }
          : componentBgPatternRaw;
      const componentBaseStyleRaw = component?.base_style || null;
      const componentContainerStyle = component?.container_style || null;
      const titleCharacterStyle = title?.character_style || null;
      const titleBaseStyleRaw = title?.base_style || null;

      // Mirror the family-card chip: keep the preset's border colour/style but
      // pin the width to 1 so chips look uniform across presets.
      const componentBaseStyle =
        componentBaseStyleRaw && typeof componentBaseStyleRaw === "object"
          ? { ...componentBaseStyleRaw, border_enabled: true, border_width: 1 }
          : componentBaseStyleRaw;
      const componentContainerStyleNoShadow =
        componentContainerStyle && typeof componentContainerStyle === "object"
          ? {
              ...componentContainerStyle,
              shadow_internal_enabled: false,
              shadow_internal_size: 0,
            }
          : componentContainerStyle;

      // Also remove base-level shadow/glow that use `box-shadow` so chips don't look elevated.
      const componentBaseStyleNoShadowGlow =
        componentBaseStyle && typeof componentBaseStyle === "object"
          ? {
              ...componentBaseStyle,
              shadow_enabled: false,
              shadow_custom_enabled: false,
              glow_enabled: false,
            }
          : componentBaseStyle;

      const { colorClass: bgClass = "", colorStyle: bgStyle = {} } = getBackgroundPatternClass(componentBgPattern);
      const { className: componentBaseClassesRaw = "", style: componentBaseStylesRaw = {} } =
        getBaseStyleClasses(componentBaseStyleNoShadowGlow);
      const rawContainerClasses = getContainerStyleClasses(componentContainerStyleNoShadow);
      // If preset doesn't define rounding, default to rounded-xl for better UI consistency.
      const needsFallbackRounding = !/\brounded(-(?!none)\S+)?\b/.test(rawContainerClasses || "");
      const componentContainerClasses = `${rawContainerClasses} ${needsFallbackRounding ? "rounded-xl" : ""}`.trim();
      const insetShadowClass = getContainerInsetShadowClass(componentContainerStyleNoShadow);
      // Only clip background to rounded corners when a rounded border is actually present.
      // Otherwise keep overflow visible so shadows/glows aren't cut off.
      const hasRoundedBorder = /\brounded(-(?!none)\S+)?\b/.test(componentContainerClasses || "");
      const overflowClass = hasRoundedBorder ? "overflow-hidden" : "overflow-visible";

      // Defensive cleanup: ensure we don't accidentally keep Tailwind shadow utilities in the selector.
      const componentBaseClasses = String(componentBaseClassesRaw || "")
        .split(" ")
        .filter((c) => c && !/^(shadow|shadow-|drop-shadow)/.test(c))
        .join(" ");
      const componentBaseStyles = { ...(componentBaseStylesRaw || {}) };
      if (componentBaseStyles && typeof componentBaseStyles === "object" && "boxShadow" in componentBaseStyles) {
        delete componentBaseStyles.boxShadow;
      }

      const { className: rawCharClasses = "", style: charStyles = {} } = getCharacterStyleClasses(titleCharacterStyle, null);
      const cleanCharClasses = String(rawCharClasses || "")
        .split(" ")
        .filter((c) => c && !/^(flex|justify-|items-|text-left|text-right|text-center|text-justify)/.test(c) && c.trim() !== "none")
        // Normalise line-height so label doesn't appear vertically off across presets.
        .filter((c) => c && !/^leading-/.test(c))
        .join(" ");

      const { className: titleBaseClasses = "", style: titleBaseStyles = {} } = getBaseStyleClasses(titleBaseStyleRaw, true);

      // IMPORTANT: selection highlight must NOT rely on Tailwind ring, because preset base_style may set inline
      // boxShadow (which would override the ring's box-shadow). Use outline instead.
      const selectionOutlineClasses = isSelected
        ? "outline outline-2 outline-blue-500 outline-offset-2"
        : "outline outline-1 outline-transparent outline-offset-2";

      return {
        cardClassName: [
          "relative",
          "w-full",
          "flex",
          "items-center",
          "justify-center",
          "p-2",
          overflowClass,
          componentContainerClasses,
          componentBaseClasses,
          selectionOutlineClasses,
        ]
          .filter(Boolean)
          .join(" "),
        cardStyle: componentBaseStyles,
        bgClass,
        bgStyle,
        insetShadowClass,
        titleClassName: `relative z-10 block text-center leading-none ${titleBaseClasses} ${cleanCharClasses}`.trim(),
        titleStyle: { ...titleBaseStyles, ...charStyles },
      };
    };

    const compactStyledCard = isCompact && !useColorAccents && labelPreview
      ? renderStyledCompactCard()
      : null;

    return (
      <div
        key={style.value}
        onClick={() => onStyleSelect(style)}
        className={
          isCompact && compactStyledCard
            ? `${baseClasses} ${compactStyledCard.cardClassName}`.trim()
            : `${baseClasses} ${stateClasses} ${isCompact ? "rounded-md border p-2" : ""}`.trim()
        }
        style={isCompact && compactStyledCard ? compactStyledCard.cardStyle : undefined}
      >
        {isCompact ? (
          compactStyledCard ? (
            <>
              {(compactStyledCard.bgClass || compactStyledCard.insetShadowClass) && (
                <div
                  className={`absolute inset-0 w-full h-full z-0 ${compactStyledCard.bgClass} ${compactStyledCard.insetShadowClass}`.trim()}
                  style={compactStyledCard.bgStyle}
                />
              )}
              <span
                className={compactStyledCard.titleClassName}
                style={compactStyledCard.titleStyle}
                title={style.name}
              >
                {style.name}
              </span>
            </>
          ) : (
            <div className="flex items-center gap-2">
              {useColorAccents ? <span className={`h-2 w-2 rounded-full ${colors.initial}`} /> : null}
              <span className={`${textColors.primary} ${isSelected ? "font-semibold" : "font-medium"}`}>
                {style.name}
              </span>
            </div>
          )
        ) : (
          <>
            {/* Selection Indicator */}
            {isSelected && (
              <div className="absolute top-2 right-2">
                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}

            {/* Style Initial Square */}
            <div className="flex items-center mb-2">
              <div className={`
                w-12 h-12 ${colors.initial} rounded-lg flex items-center justify-center mr-3
              `}>
                <span className="text-white font-bold text-lg">
                  {style.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className={`font-heading ${typography.body} font-bold ${isSelected ? colors.text : textColors.primary}`}>
                  {style.name}
                </h3>
              </div>
            </div>

            {/* Description: show for all cards when parent grid is hovered (desktop); collapse height when hidden */}
            <p className={`${typography.bodySmall} ${isSelected ? colors.text : textColors.secondary} hidden xl:block xl:max-h-0 xl:overflow-hidden xl:group-hover:max-h-20 xl:group-hover:opacity-100 xl:transition-all xl:duration-150`}> 
              {style.description}
            </p>
          </>
        )}
      </div>
    );
  };

  // In compact mode:
  // - When there are multiple pages, keep chip widths consistent across pages (last page should NOT stretch).
  // - When there's only one page, it's nicer to let chips expand to fill the row.
  const compactColumns =
    totalPages > 1 ? ITEMS_PER_PAGE : Math.max(1, currentPageItems.length || 0);
  const gridClassName = isCompact ? "grid gap-2 w-full" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 group";

  const selectionGrid = (
    <div
      className={gridClassName}
      style={isCompact ? { gridTemplateColumns: `repeat(${compactColumns}, minmax(0, 1fr))` } : undefined}
    >
      {isCompact
        ? currentPageItems.map((family) => renderFamilyCard(family))
        : currentPageItems.map((style) => renderStyleCard(style))}
    </div>
  );

  const groupStylesToFamilies = (groupStyles) => {
    const seen = new Map();
    const order = [];
    for (const style of groupStyles) {
      const key = style.family?.key || style.value;
      if (!seen.has(key)) {
        const allFamilyStyles = styles.filter((s) => (s.family?.key || s.value) === key);
        seen.set(key, {
          key,
          primary: style.family?.primary || style.value,
          membersMeta: style.family?.members || [{ preset: style.value, name: style.name, colors: [] }],
          styles: allFamilyStyles,
        });
        order.push(key);
      }
    }
    return order.map((k) => seen.get(k));
  };

  const expandedGrid = (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {expandedStyleColumns.map((columnGroups, columnIndex) => (
        <div key={`style-group-column-${columnIndex}`} className="min-w-0 space-y-4">
          {columnGroups.map((group) => (
            <section
              key={group.key}
              aria-labelledby={`style-group-heading-${group.key}`}
              className={`min-w-0 rounded-xl border ${borders.default} ${surfaces.secondary} p-3`}
            >
              <h3
                id={`style-group-heading-${group.key}`}
                className={`${typography.overline} ${textColors.tertiary} mb-3`}
              >
                {group.label}
              </h3>
              <div className="grid gap-2">
                {groupStylesToFamilies(group.styles).map((family) => renderFamilyCard(family))}
              </div>
            </section>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className={isCompact ? "space-y-2" : "space-y-3"}>
      {/* Style Options Grid */}
      {isCompact ? (
        isExpanded ? (
          <div className="flex items-start gap-2">
            <div className="flex-1">{expandedGrid}</div>
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              aria-label="Collapse styles"
              title="Collapse"
              aria-expanded="true"
              className={`p-2 rounded-full border ${borders.default} transition-colors ${textColors.secondary} ${textColors.hover} ${surfaces.hover}`}
            >
              <ChevronUpIcon className="w-4 h-4" />
            </button>
          </div>
        ) : totalPages > 1 ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="min-w-0 flex flex-1 items-center gap-2">
              <button
                type="button"
                onClick={goToPrevPage}
                disabled={currentPage === 0}
                className={`p-2 rounded-full border ${borders.default} transition-colors ${
                  currentPage === 0
                    ? `${textColors.muted} cursor-not-allowed`
                    : `${textColors.secondary} ${textColors.hover} ${surfaces.hover}`
                }`}
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <div className="min-w-0 flex-1 flex justify-center">{selectionGrid}</div>
              <button
                type="button"
                onClick={goToNextPage}
                disabled={currentPage === totalPages - 1}
                className={`p-2 rounded-full border ${borders.default} transition-colors ${
                  currentPage === totalPages - 1
                    ? `${textColors.muted} cursor-not-allowed`
                    : `${textColors.secondary} ${textColors.hover} ${surfaces.hover}`
                }`}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              aria-label="View more styles"
              title="View more styles"
              aria-expanded="false"
              className={`inline-flex w-full sm:w-auto shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full border ${borders.default} px-3 py-2 transition-colors ${textColors.secondary} ${textColors.hover} ${surfaces.hover} ${typography.bodySmall}`}
            >
              <GridMenuIcon className="w-4 h-4" />
              <span>View more</span>
            </button>
          </div>
        ) : (
          selectionGrid
        )
      ) : (
        selectionGrid
      )}

      {/* Pagination */}
      {!isCompact && totalPages > 1 && (
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={goToPrevPage}
            disabled={currentPage === 0}
            className={`
              p-2 rounded-full transition-colors
              ${currentPage === 0 
                ? `${textColors.muted} cursor-not-allowed` 
                : `${textColors.secondary} ${textColors.hover} ${surfaces.hover}`
              }
            `}
          >
            <ChevronLeftIcon />
          </button>

          <div className="flex space-x-1">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i)}
                className={`
                  w-8 h-8 rounded-full ${typography.bodySmall} font-medium transition-colors
                  ${i === currentPage 
                    ? 'bg-blue-600 !text-white' 
                    : `${textColors.secondary} ${surfaces.hover}`
                  }
                `}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages - 1}
            className={`
              p-2 rounded-full transition-colors
              ${currentPage === totalPages - 1 
                ? `${textColors.muted} cursor-not-allowed` 
                : `${textColors.secondary} ${textColors.hover} ${surfaces.hover}`
              }
            `}
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}

      {/* Page Info */}
      {!isCompact && totalPages > 1 && (
        <div className={`text-center ${typography.bodySmall} ${textColors.secondary}`}>
          Showing {currentPage * ITEMS_PER_PAGE + 1} - {Math.min((currentPage + 1) * ITEMS_PER_PAGE, displayItems.length)} of {displayItems.length} styles
        </div>
      )}
    </div>
  );
};

export default StyleSelection; 